/**
 * Pure helpers for the wallet ledger (ENG-27365).
 *
 * No React, no network — everything here is a function of its arguments so the
 * ledger (ENG-27315) can be tested without mounting anything. Open-advance entry
 * helpers live in `openAdvance.ts`.
 */
import type {
  LedgerRow,
  TransactionContext,
  TransactionsModel,
  TransferType,
} from "../types/wallet";

// ---------------------------------------------------------------------------
// Transaction context
// ---------------------------------------------------------------------------

/**
 * Reads one stamped context value.
 *
 * Gringotts returns `transactionContexts` as rows of `{attributeName,
 * attributeValue, deleted, …}` — verified against beta. Deleted attributes are
 * skipped. An object map is still tolerated so a shape change degrades to a
 * missing label rather than a crash on a live ledger.
 */
export function readContext(
  contexts: TransactionContext[] | Record<string, string> | undefined | null,
  key: string,
): string | undefined {
  if (!contexts) return undefined;
  if (Array.isArray(contexts)) {
    const hit = contexts.find((c) => c?.attributeName === key && !c?.deleted);
    return hit?.attributeValue ?? undefined;
  }
  const value = (contexts as Record<string, string>)[key];
  return value == null ? undefined : String(value);
}

/**
 * Short category for the ledger's Type chip: how the money entered or left the
 * wallet, in one or two words. Paired with `transactionDescription`, a row reads
 * as "Advance · PO FSPO-28-26-00015".
 *
 * Open advance reaches the ledger only as `open_advance_refund` (VIP-WALREF):
 * since ENG-27743 parking and set-off are not mirrored to Gringotts at all, so
 * they appear only in the Wallet tab's entries section (ENG-27316).
 */
export type WalletTxnCategory =
  | "invoice"
  | "credit_note"
  | "po_advance"
  | "po_advance_reversal"
  | "advance_adjustment"
  | "advance_adjustment_reversal"
  | "payment_request"
  | "open_advance_refund"
  | "credit_note_refund"
  | "other";

const CATEGORY_LABELS: Record<Exclude<WalletTxnCategory, "other">, string> = {
  invoice: "Invoice",
  credit_note: "Credit note",
  po_advance: "Advance",
  // VIP-POADVREV (ENG-28340): debit undoing a PO's advance credit when the
  // advance is taken back — a cancelled PO's unpaid advance, or a rejected
  // advance request.
  po_advance_reversal: "Advance reversed",
  advance_adjustment: "Adjustment",
  advance_adjustment_reversal: "Reversal",
  payment_request: "Payment",
  open_advance_refund: "Refund",
  // VIP-CNREF (ENG-28721): credit for what a credit note's refund UTR sent back,
  // settling that credit note's debit (ENG-28637).
  credit_note_refund: "Refund",
};

/**
 * Categorises a row for the Type chip. An unrecognised `payment_type` keeps its
 * humanised text under the "other" key, so a new VIP event is still readable
 * and simply renders in the neutral colour.
 */
export function paymentTypeCategory(
  contexts: TransactionContext[] | Record<string, string> | undefined | null,
): { key: WalletTxnCategory; label: string } {
  const paymentType = readContext(contexts, "payment_type");
  if (paymentType && paymentType in CATEGORY_LABELS) {
    const key = paymentType as Exclude<WalletTxnCategory, "other">;
    return { key, label: CATEGORY_LABELS[key] };
  }
  return {
    key: "other",
    label: paymentType ? humanisePaymentType(paymentType) : "Other",
  };
}

/** "some_new_event" → "Some new event", so an unknown type still reads sensibly. */
function humanisePaymentType(raw: string): string {
  const spaced = raw.replace(/_/g, " ").trim();
  if (!spaced) return "Other";
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** "Invoice TST24120005 approved", or "Invoice approved" without a number. */
function documentEvent(document: string, number: string | undefined, verb: string): string {
  return number ? `${document} ${number} ${verb}` : `${document} ${verb}`;
}

/**
 * Plain-language description for a ledger row: what happened, then the PO —
 * "Invoice TST24120005 approved · PO SO-179-241200005".
 *
 * The sentence names the document by its number, so it adds to the Type chip
 * rather than repeating it. VIP stamps `invoice_number` / `credit_note_number`
 * (and `po_code` on credit notes) since ENG-27454; Gringotts rows are
 * immutable, so older rows fall back to "Invoice approved · PO …" and old credit
 * notes to "Credit note approved". An unknown `payment_type` lists just the
 * documents, since there is no event wording for it.
 *
 * The open-advance refund (VIP-WALREF) names the credit note it came from and
 * the vendor's UTR; without the number it still reads "Held credit refunded".
 * A credit note refunded by UTR at approval (VIP-CNREF) reads "Credit note 444
 * refunded · UTR … · PO …".
 *
 * `invoice_id`, `credit_note_id` and `wallet_entry_id` are internal primary
 * keys and are deliberately never shown ("Invoice #3192" means nothing to a
 * vendor).
 * Returns "" when nothing describes the row.
 */
export function transactionDescription(
  contexts: TransactionContext[] | Record<string, string> | undefined | null,
): string {
  const paymentType = readContext(contexts, "payment_type");
  const invoiceNumber = readContext(contexts, "invoice_number");
  const creditNoteNumber = readContext(contexts, "credit_note_number");
  const poCode = readContext(contexts, "po_code");
  const po = poCode && `PO ${poCode}`;

  switch (paymentType) {
    case "invoice":
      return join(documentEvent("Invoice", invoiceNumber, "approved"), po);
    case "credit_note":
      return join(documentEvent("Credit note", creditNoteNumber, "approved"), po);
    case "po_advance":
      // The PO is the subject here, so it goes in the sentence, not after it.
      return poCode ? `Advance on PO ${poCode}` : "Advance on PO";
    case "po_advance_reversal":
      // Mirrors the credit it undoes, so the pair reads as one story.
      return poCode ? `Advance on PO ${poCode} reversed` : "Advance on PO reversed";
    // ENG-27745 stamps invoice_number on these three; rows posted before it
    // never gain it, so the PO-only wording stays as the fallback.
    case "advance_adjustment":
      return join(
        invoiceNumber ? `Advance adjusted against invoice ${invoiceNumber}` : "Advance adjusted",
        po,
      );
    case "advance_adjustment_reversal":
      return join(
        invoiceNumber ? `Advance adjustment on invoice ${invoiceNumber} reversed` : "Advance adjustment reversed",
        po,
      );
    case "payment_request":
      return join(invoiceNumber ? `Payment made for invoice ${invoiceNumber}` : "Payment made", po);
    case "open_advance_refund": {
      const utr = readContext(contexts, "refund_utr");
      // Only the unused balance is refunded, so the credit note is named as the
      // source rather than as the thing refunded.
      const event = creditNoteNumber ? `Held credit from ${creditNoteNumber} refunded` : "Held credit refunded";
      return join(event, utr && `UTR ${utr}`, po);
    }
    case "credit_note_refund": {
      const utr = readContext(contexts, "refund_utr");
      return join(documentEvent("Credit note", creditNoteNumber, "refunded"), utr && `UTR ${utr}`, po);
    }
    default:
      return join(
        invoiceNumber && `Invoice ${invoiceNumber}`,
        creditNoteNumber && `Credit note ${creditNoteNumber}`,
        po,
      );
  }
}

function join(...parts: Array<string | undefined>): string {
  return parts.filter(Boolean).join(" · ");
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/**
 * Wallet balance immediately after this row.
 *
 * Gringotts reports the balance per side of the transfer: on a credit the
 * wallet is the destination, on a debit it is the source. Keyed on `direction`
 * (the query the row came from), never on the row's own `transferType`, which
 * carries Gringotts' internal vocabulary such as "WALLET_TO_OUTSIDE". Returns
 * undefined when the relevant side is absent rather than the wrong side.
 */
export function runningBalance(row: LedgerRow): number | undefined {
  return row.direction === "credit"
    ? row.destinationWalletBalance
    : row.sourceWalletBalance;
}

/**
 * When a row was applied to the wallet balance — the key the ledger is ordered
 * and trimmed on (ENG-27798).
 *
 * Gringotts stamps `updatedAt` in the same save that applies the balance, so it
 * orders rows the way their balance snapshots chain; `transactionDate` does
 * not, as near-simultaneous rows take the wallet lock out of order. A row
 * without `updatedAt` falls back to its business date, and one with neither
 * sorts oldest rather than being dropped.
 */
export function appliedAt(row: TransactionsModel): number {
  return row.updatedAt ?? row.transactionDate ?? 0;
}

/**
 * Merges the credit and debit pages into one newest-first ledger.
 *
 * The Gringotts search requires `transferType`, so a full ledger is always two
 * calls. Rows are ordered as the server pages them — `appliedAt` descending,
 * then `id` descending (see `LEDGER_SORT`) — and any remaining tie keeps input
 * order, so the list doesn't reshuffle between renders.
 */
export function mergeLedgerPages(
  credits: TransactionsModel[] | undefined | null,
  debits: TransactionsModel[] | undefined | null,
): LedgerRow[] {
  const tag = (rows: TransactionsModel[] | undefined | null, direction: TransferType) =>
    (rows ?? []).map((row) => ({ ...row, direction }));
  const combined = [...tag(credits, "credit"), ...tag(debits, "debit")];
  // Array.prototype.sort is stable, so a full tie keeps input order.
  return combined.sort((a, b) => appliedAt(b) - appliedAt(a) || b.id - a.id);
}

/**
 * Gringotts appears to require a date window on both `/summary` and
 * `/transactions/search` — every call verified on beta carried one, and
 * omitting it returns 400. So "no filter" means "all history" rather than "no
 * parameters", and this is the floor: comfortably before any wallet existed.
 */
export const WALLET_HISTORY_START = "2020-01-01";

/** `YYYY-MM-DD` in local time, the format Gringotts' date params take. */
export function toIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * The `endDate` to send the transactions search for an inclusive "To" day.
 *
 * Gringotts filters the search with `transactionDate.le:<endDate>`, and a bare
 * date means midnight — so sending the "To" day itself drops everything that
 * happened on it, including every transaction posted today under the default
 * range. Sending the following day keeps the whole day. (`/summary` totals by
 * whole day and is already inclusive, so it takes the "To" day unchanged.)
 */
export function searchEndDate(inclusiveEnd: string): string {
  const [year, month, day] = inclusiveEnd.split("-").map(Number);
  return toIsoDate(new Date(year, month - 1, day + 1));
}

/** Formats Gringotts' epoch-ms `transactionDate` as "DD Mon YYYY". */
export function formatEpochDate(ms: number | undefined | null): string {
  if (ms == null) return "—";
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/**
 * The window's credit / debit. No balance on purpose: the summary's balance is
 * credit − debit inside the window, not the wallet's (ENG-28276), so nothing
 * should be able to read it.
 */
export interface WalletTotals {
  credit?: number;
  debit?: number;
}

/** Reads a number from the first key that carries one. */
function firstNumber(source: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "number" && !Number.isNaN(value)) return value;
  }
  return undefined;
}

/**
 * Pulls the window's credit/debit out of a wallet summary.
 *
 * Gringotts' summary returns camelCase (`totalCredit`, `totalDebit` —
 * verified on beta, see `WalletSummaryDTO`), while VIP's wallet
 * read uses snake_case (`total_credit`). Both spellings, plus the bare
 * `credit`/`debit`, are still accepted so a casing change on either service
 * degrades to the other spelling rather than silently rendering dashes.
 */
export function readSummaryTotals(data: unknown): WalletTotals {
  if (!data || typeof data !== "object") return {};
  const source = data as Record<string, unknown>;
  return {
    credit: firstNumber(source, ["totalCredit", "total_credit", "credit"]),
    debit: firstNumber(source, ["totalDebit", "total_debit", "debit"]),
  };
}

export interface LedgerSide {
  rows: TransactionsModel[];
  /** True when the server holds rows older than the ones fetched. */
  hasMore: boolean;
}

/**
 * Trims a merged ledger to the window that is provably complete.
 *
 * Credits and debits are paged independently, so merging page N of each does
 * NOT give the globally newest 2N rows: if one side still has older rows on the
 * server, any merged row older than that side's oldest fetched row might have
 * unfetched neighbours, and showing it would present a ledger with silent gaps.
 *
 * The safe boundary is therefore the NEWEST "oldest fetched row" among the
 * sides that still have more — everything at or after it is complete. A side
 * that is fully fetched constrains nothing; nor does one that returned no rows,
 * where no boundary can be established and the best available answer is to show
 * what we have.
 */
export function trimToCompleteWindow(
  merged: LedgerRow[],
  sides: LedgerSide[],
): LedgerRow[] {
  let boundary: number | undefined;
  for (const side of sides) {
    if (!side.hasMore || side.rows.length === 0) continue;
    const oldest = Math.min(...side.rows.map(appliedAt));
    boundary = boundary === undefined ? oldest : Math.max(boundary, oldest);
  }
  if (boundary === undefined) return merged;
  const cutoff = boundary;
  return merged.filter((row) => appliedAt(row) >= cutoff);
}
